// src/components/ChartOfAccounts.js — QuickBooks-style Chart of Accounts
// [marker: coa v1]
// Every account (banks, credit cards, income, expense, …) in one Excel-style list.
// Add, rename, renumber, move under a parent, make inactive, delete unused.
import React, { useState, useMemo } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import {
  useAccounts, invalidateAccounts, AccountSelect, TYPE_ORDER, TYPE_LABELS,
  BALANCE_SHEET_TYPES, CREDIT_NORMAL, fmtMoney, displayBalance,
} from './AccountSelect';

const TYPES_WITH_OPENING = ['bank', 'credit_card', 'asset', 'fixed_asset', 'other_asset', 'other_liability', 'loan'];
const SYSTEM_LABELS = {
  accounts_receivable: 'Used for unpaid invoices',
  accounts_payable: 'Used for unpaid bills',
  undeposited_funds: 'Holds payments until you deposit them',
  opening_balance_equity: 'Balancing account for opening balances',
  retained_earnings: 'Prior years’ profit',
};

export default function ChartOfAccounts({ canEdit, onChanged, onOpenRegister }) {
  const [showInactive, setShowInactive] = useState(false);
  const { accounts, reload } = useAccounts({ includeInactive: true });
  const [editing, setEditing] = useState(null); // account | {} for new | null
  const [newType, setNewType] = useState(null);

  const rows = useMemo(() => accounts.filter(a => showInactive || a.is_active), [accounts, showInactive]);
  const changed = () => { invalidateAccounts(); reload(); onChanged && onChanged(); };

  const columns = [
    { key: 'name', label: 'Account', width: 340, get: a => a.fullName,
      render: a => (
        <span style={{ paddingLeft: a.depth * 18, display: 'inline-flex', alignItems: 'center', gap: 6,
                       color: a.is_active ? undefined : 'var(--gray-400)', fontWeight: a.depth === 0 ? 600 : 400 }}>
          {a.depth > 0 && <span style={{ color: 'var(--gray-300)' }}>└</span>}
          {a.name}
          {a.account_last_four && <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>••{a.account_last_four}</span>}
          {!a.is_active && <span className="badge badge-muted" style={{ fontSize: '0.65rem' }}>inactive</span>}
          {a.system_key && <span title={SYSTEM_LABELS[a.system_key] || 'Used by the system'} style={{ fontSize: '0.72rem' }}>⚙</span>}
        </span>
      ) },
    { key: 'account_type', label: 'Type', width: 170, text: a => TYPE_LABELS[a.account_type] || a.account_type,
      get: a => TYPE_ORDER.indexOf(a.account_type) },
    { key: 'account_number', label: 'Number', width: 90 },
    { key: 'balance', label: 'Balance', width: 120, type: 'money', align: 'right',
      get: a => (BALANCE_SHEET_TYPES.includes(a.account_type) ? displayBalance(a) : null) },
    { key: 'line_count', label: 'Transactions', width: 105, type: 'number' },
    { key: 'item_count', label: 'Items Linked', width: 100, type: 'number',
      get: a => (['income', 'other_income'].includes(a.account_type) ? a.item_count : null) },
    { key: 'description', label: 'Description', width: 240 },
    { key: 'parent_name', label: 'Sub-account of', width: 160, defaultHidden: true },
    { key: 'is_active', label: 'Active', width: 70, type: 'bool', defaultHidden: true },
    { key: 'actions', label: '', width: 170, text: () => '',
      render: a => (
        <span style={{ display: 'inline-flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          <a className="btn btn-ghost btn-sm" style={{ padding: '0 6px', height: 24, lineHeight: '24px' }} href={`/bookkeeping-reports?account=${a.id}`} title="Every transaction in this account">Report</a>{/* [marker: account-reports v1] */}
          {onOpenRegister && ['bank', 'credit_card'].includes(a.account_type) && (
            <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', height: 24 }} onClick={() => onOpenRegister(a)}>Register</button>
          )}
          {canEdit && <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', height: 24 }} onClick={() => setEditing(a)}>Edit</button>}
        </span>
      ) },
  ];

  return (
    <div>
      <DataGrid
        gridKey="chart-of-accounts"
        rows={rows}
        loading={!accounts.length}
        columns={columns}
        onRowClick={canEdit ? (a => setEditing(a)) : undefined}
        height="calc(100vh - 300px)"
        emptyText="No accounts."
        toolbarLeft={
          <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
            {canEdit && (
              <select className="form-select" style={{ width: 'auto', height: 32 }} value=""
                      onChange={e => { if (e.target.value) { setNewType(e.target.value); setEditing({}); } }}>
                <option value="">+ New account…</option>
                <option value="bank">Bank account</option>
                <option value="credit_card">Credit card</option>
                <option value="income">Income account</option>
                <option value="expense">Expense account</option>
                <option value="__other">Other type…</option>
              </select>
            )}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
          </span>
        }
      />
      <p className="form-hint" style={{ marginTop: 8 }}>
        ⚙ = an account the system posts to automatically. Balances show for bank, card, asset, liability and equity accounts;
        income and expense totals are on the reports.
      </p>

      {editing && (
        <AccountModal
          account={editing.id ? editing : null}
          initialType={editing.id ? null : (newType === '__other' ? 'income' : newType)}
          accounts={accounts}
          onClose={() => { setEditing(null); setNewType(null); }}
          onSaved={() => { setEditing(null); setNewType(null); changed(); }}
        />
      )}
    </div>
  );
}

function AccountModal({ account, initialType, accounts, onClose, onSaved }) {
  const isEdit = !!account;
  const type0 = account?.account_type || initialType || 'income';
  const credit0 = CREDIT_NORMAL.includes(type0);
  const [form, setForm] = useState({
    name: account?.name || '',
    accountType: type0,
    parentAccountId: account?.parent_account_id || null,
    accountNumber: account?.account_number || '',
    accountLastFour: account?.account_last_four || '',
    description: account?.description || '',
    openingBalance: account && parseFloat(account.opening_balance) ? String(credit0 ? -parseFloat(account.opening_balance) : parseFloat(account.opening_balance)) : '',
    openingBalanceDate: account?.opening_balance_date ? String(account.opening_balance_date).slice(0, 10) : '',
    isActive: account ? account.is_active : true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const used = isEdit && (account.line_count > 0 || account.child_count > 0);
  const typeLocked = isEdit && (account.line_count > 0 || account.child_count > 0 || account.system_key);
  // parents: same type, not itself or its descendants
  const descendants = useMemo(() => {
    if (!isEdit) return [];
    const out = new Set([String(account.id)]);
    let grew = true;
    while (grew) {
      grew = false;
      accounts.forEach(a => { if (a.parent_account_id && out.has(String(a.parent_account_id)) && !out.has(String(a.id))) { out.add(String(a.id)); grew = true; } });
    }
    return Array.from(out);
  }, [isEdit, account, accounts]);
  const parentChoices = accounts.filter(a => a.account_type === form.accountType && a.is_active);
  const showOpening = TYPES_WITH_OPENING.includes(form.accountType);
  const showLastFour = ['bank', 'credit_card'].includes(form.accountType);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Account name is required.');
    const credit = CREDIT_NORMAL.includes(form.accountType);
    const ob = form.openingBalance === '' ? 0 : parseFloat(form.openingBalance);
    if (isNaN(ob)) return toast.error('Opening balance must be a number.');
    const payload = {
      name: form.name.trim(),
      accountType: form.accountType,
      parentAccountId: form.parentAccountId || null,
      accountNumber: form.accountNumber,
      accountLastFour: showLastFour ? form.accountLastFour : '',
      description: form.description,
      openingBalance: showOpening ? (credit ? -ob : ob) : 0,
      openingBalanceDate: showOpening && ob ? (form.openingBalanceDate || null) : null,
    };
    if (isEdit) payload.isActive = !!form.isActive;
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/bookkeeping/accounts/${account.id}`, payload);
      else await api.post('/bookkeeping/accounts', payload);
      toast.success(isEdit ? 'Account saved.' : 'Account added.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the account.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the account "${account.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.delete(`/bookkeeping/accounts/${account.id}`);
      toast.success('Account deleted.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete the account.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Account' : 'New Account'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {account?.system_key && (
            <div style={{ fontSize: '0.82rem', background: 'var(--gray-50)', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
              ⚙ {SYSTEM_LABELS[account.system_key] || 'The system posts to this account automatically.'} You can rename it, but not delete it or change its type.
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Account Type *</label>
              <select className="form-select" value={form.accountType} disabled={typeLocked}
                      onChange={e => { set('accountType', e.target.value); set('parentAccountId', null); }}>
                {TYPE_ORDER.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
              {typeLocked && !account?.system_key && <p className="form-hint">Type can’t change once an account has transactions or sub-accounts.</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Account Number</label>
              <input className="form-input" value={form.accountNumber} onChange={e => set('accountNumber', e.target.value)} placeholder="Optional, e.g. 4000" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Account Name *</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus
                   placeholder={form.accountType === 'bank' ? 'e.g. Chase Operating Checking' : form.accountType === 'credit_card' ? 'e.g. Amex Business Card' : ''} />
          </div>
          <div className="form-group">
            <label className="form-label">Sub-account of</label>
            <AccountSelect accounts={parentChoices} value={form.parentAccountId} onChange={v => set('parentAccountId', v)}
                           excludeIds={descendants} placeholder="— None (top level) —" />
          </div>
          {showLastFour && (
            <div className="form-group">
              <label className="form-label">Last 4 digits of the account / card</label>
              <input className="form-input" style={{ maxWidth: 140 }} maxLength={4} value={form.accountLastFour}
                     onChange={e => set('accountLastFour', e.target.value.replace(/\D/g, ''))} placeholder="1234" />
            </div>
          )}
          {showOpening && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{form.accountType === 'credit_card' || form.accountType === 'loan' || form.accountType === 'other_liability' ? 'Opening balance owed' : 'Opening balance'}</label>
                <input className="form-input" type="number" step="0.01" value={form.openingBalance} onChange={e => set('openingBalance', e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">As of</label>
                <input className="form-input" type="date" value={form.openingBalanceDate} onChange={e => set('openingBalanceDate', e.target.value)} />
              </div>
            </div>
          )}
          {showOpening && <p className="form-hint" style={{ marginTop: -6 }}>Leave at 0 if the account’s history will come over from QuickBooks.</p>}
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          {isEdit && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isActive} disabled={!!account.system_key} onChange={e => set('isActive', e.target.checked)} />
              Active <span style={{ color: 'var(--gray-500)' }}>— untick to hide it from lists (it stays in past reports)</span>
            </label>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {isEdit && !account.system_key && !used
            ? <button className="btn btn-danger" onClick={remove} disabled={saving}>Delete</button>
            : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Add Account')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
